/**
 * A2 — Primeros pasos. Una lista de lo que falta para empezar a trabajar con datos
 * reales; cada paso se marca solo al cumplirse y lleva a la pantalla donde se hace.
 * Siempre accesible desde Ajustes → Primeros pasos.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, Building2, CheckCircle2, Circle, Loader2, Mail, Users } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import type { ClientListResponse, IssuerDto } from '@toga/shared'

interface Status {
  issuerReady: boolean
  clients: number
  mailAccounts: number
  cases: number
}

async function safe<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p
  } catch {
    return null
  }
}

function Step({
  done,
  icon,
  title,
  children,
  to,
  cta,
  disabled,
}: {
  done: boolean
  icon: ReactNode
  title: string
  children: ReactNode
  to: string
  cta: string
  disabled?: boolean
}) {
  return (
    <li className="surface p-5 flex flex-wrap items-center gap-4">
      <span className={done ? 'text-emerald-400' : 't-faint'}>
        {done ? <CheckCircle2 size={22} /> : <Circle size={22} />}
      </span>
      <span className="w-9 h-9 rounded-lg grid place-items-center bg-toga-500/15 text-toga-300 shrink-0">
        {icon}
      </span>
      <div className="min-w-0 flex-1 basis-64">
        <h3 className="text-sm font-semibold t-primary">{title}</h3>
        <p className="t-muted text-xs mt-0.5">{children}</p>
      </div>
      {disabled ? (
        <span className="t-faint text-xs">Lo hace un administrador</span>
      ) : (
        <Link
          to={to}
          className={[
            'px-3.5 py-2 rounded-lg text-sm',
            done
              ? 'border border-base t-secondary hover:bg-hover'
              : 'font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga',
          ].join(' ')}
        >
          {done ? 'Revisar' : cta}
        </Link>
      )}
    </li>
  )
}

export default function OnboardingPage() {
  const auth = useAuth()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  const isAdmin = role === 'ADMIN'
  const canImport = role === 'ADMIN' || role === 'ABOGADO'
  const [status, setStatus] = useState<Status | null>(null)

  useEffect(() => {
    let alive = true
    void (async () => {
      const [issuers, clients, mail, cases] = await Promise.all([
        safe(api.get<{ items: IssuerDto[] }>('/issuers')),
        safe(api.get<ClientListResponse>('/clients?pageSize=1')),
        safe(api.get<{ items: unknown[] }>('/email-accounts')),
        safe(api.get<{ total: number }>('/cases?pageSize=1')),
      ])
      if (!alive) return
      setStatus({
        issuerReady: !!issuers?.items.some((i) => (i.nif ?? '').trim() !== ''),
        clients: clients?.total ?? 0,
        mailAccounts: mail?.items.length ?? 0,
        cases: cases?.total ?? 0,
      })
    })()
    return () => {
      alive = false
    }
  }, [])

  return (
    <div className="space-y-6 animate-slideUp max-w-3xl">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">Primeros pasos</h2>
        <p className="t-muted text-sm mt-1">
          Lo que conviene dejar listo antes de trabajar con datos reales. Cada paso se marca solo
          cuando está hecho.
        </p>
      </header>

      {!status ? (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Comprobando…
        </div>
      ) : (
        <>
          <ol className="space-y-3">
            <Step
              done={status.issuerReady}
              icon={<Building2 size={18} />}
              title="Datos del bufete y del emisor de facturas"
              to="/settings/firm"
              cta="Completar"
              disabled={!isAdmin}
            >
              Nombre, NIF/CIF y dirección fiscal: salen en presupuestos y facturas.
            </Step>
            <Step
              done={status.clients > 0}
              icon={<Users size={18} />}
              title="Importar clientes y contactos"
              to="/clients/import"
              cta="Importar desde Excel"
              disabled={!canImport}
            >
              Sube tu cartera desde un Excel (o crea clientes uno a uno). Verás una vista previa
              antes de guardar y podrás deshacer.
            </Step>
            <Step
              done={status.mailAccounts > 0}
              icon={<Mail size={18} />}
              title="Conectar tu correo"
              to="/inbox"
              cta="Conectar"
            >
              Para que los correos con tus clientes entren solos en su ficha. Las llamadas,
              reuniones o WhatsApp se registran a mano; los canales se ajustan en Ajustes → Canales.
            </Step>
            <Step
              done={status.cases > 0}
              icon={<Briefcase size={18} />}
              title="Abrir el primer expediente"
              to="/cases"
              cta="Ir a expedientes"
            >
              Cada expediente cuelga de un cliente: allí van documentos, plazos, tareas y facturas.
            </Step>
          </ol>

          <section className="surface p-5">
            <h3 className="text-sm font-semibold t-primary mb-2">Resumen</h3>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              {[
                ['Clientes', status.clients],
                ['Expedientes', status.cases],
                ['Cuentas de correo (tuyas)', status.mailAccounts],
                ['Emisor con NIF', status.issuerReady ? 'Sí' : 'No'],
              ].map(([label, value]) => (
                <div key={label as string} className="rounded-lg border border-base px-3 py-2.5">
                  <dd className="text-xl font-semibold t-primary">{value}</dd>
                  <dt className="t-muted text-xs">{label}</dt>
                </div>
              ))}
            </dl>
          </section>

          <Link to="/dashboard" className="t-muted text-sm hover:underline">
            Ir al panel
          </Link>
        </>
      )}
    </div>
  )
}
